<!--
  Public account-deletion page. Renders the copy from
  agent-os/specs/2026-05-29-account-deletion/store-copy.md § A.

  This is the URL pasted into Play Console — keep it no-login.
-->
<main class="account-deletion-page">
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-lg-8">

        <header class="mb-4">
          <a routerLink="/login" class="text-decoration-none text-secondary small">
            ← American Drone Network
          </a>
          <h1 class="mt-3 mb-2">Deleting your American Drone Network account</h1>
          <p class="lead text-secondary mb-0">
            How to delete your account and what data we retain for FAA compliance.
          </p>
        </header>

        <section class="mb-4">
          <p>
            American Drone Network (ADN) is a two-sided marketplace connecting
            farmers (who post spray-service requests for their own fields) with
            independent, FAA-certified drone applicators ("pilots") who bid on
            and perform aerial agricultural application services on-site,
            outside the app. Payments for those services are processed through
            Stripe Connect — ADN itself never holds or moves funds.
          </p>
          <p>
            ADN lets you delete your account and personal data directly in the
            mobile app or here on the web. Because aerial agricultural
            application is a regulated activity, certain flight and
            application records are retained as required by law (see
            <a href="#what-we-keep">What we keep</a>, below).
          </p>
        </section>

        <section class="mb-4">
          <h2 class="h4 mb-3">Delete your account on the web</h2>
          <p>If you can sign in:</p>
          <ol>
            <li>
              <a routerLink="/login" [queryParams]="{ returnUrl: '/profile/delete-account' }">
                Sign in to American Drone Network
              </a>.
            </li>
            <li>Go to <strong>Profile</strong>.</li>
            <li>Click <strong>Delete Account</strong>.</li>
            <li>
              Review what will be removed and retained, confirm your password,
              and click <strong>Delete my account</strong>.
            </li>
            <li>
              You'll be shown a one-time <strong>recovery code</strong>. Save it
              — it is the only way to regain access later.
            </li>
          </ol>
        </section>

        <section class="mb-4">
          <h2 class="h4 mb-3">Delete your account in the mobile app</h2>
          <ol>
            <li>Open the American Drone Network app and sign in.</li>
            <li>Go to <strong>Profile</strong>.</li>
            <li>Tap <strong>Delete Account</strong>.</li>
            <li>
              Review what will be removed and retained, confirm your password,
              and tap <strong>Delete my account</strong>.
            </li>
            <li>
              You'll be shown a one-time <strong>recovery code</strong>. Save it
              — it is the only way to regain access later.
            </li>
          </ol>
        </section>

        <section class="mb-4 callout-warning">
          <h2 class="h5 mb-2">Can't access the app or sign in?</h2>
          <p class="mb-2">
            Email
            <a [href]="mailtoLink"><strong>{{ supportEmail }}</strong></a>
            from the address on your account and request account deletion. We'll
            process it and confirm by email.
          </p>
        </section>

        <section class="mb-4">
          <h2 class="h4 mb-3">What we delete</h2>
          <ul>
            <li>Your login/credentials and access to the app.</li>
            <li>Your personal profile data that we are not legally required to retain.</li>
            <li>Organizations where you are the only member are closed.</li>
          </ul>
        </section>

        <section id="what-we-keep" class="mb-4">
          <h2 class="h4 mb-3">What we keep (and why)</h2>
          <ul>
            <li>
              Flight records, spray/application records, and FAA/state
              compliance reports, plus the identity information needed to
              attribute those records to the responsible person. These are
              retained <em>{{ retentionBasis }}</em> for <em>{{ retentionPeriod }}</em>.
            </li>
            <li>
              Deleting your account removes your access to this data; it does
              not erase records we are legally obligated to maintain.
            </li>
          </ul>
        </section>

        <section class="mb-4">
          <h2 class="h4 mb-3">Recovering your account</h2>
          <p>
            Account recovery is handled by ADN support. Contact
            <a [href]="mailtoLink"><strong>{{ supportEmail }}</strong></a>
            with your account email and the recovery code you saved during
            deletion.
          </p>
        </section>

        <footer class="mt-5 pt-4 border-top">
          <p class="text-secondary small mb-0">
            Questions about your data? Contact
            <a [href]="mailtoLink">{{ supportEmail }}</a>.
          </p>
        </footer>

      </div>
    </div>
  </div>
</main>
